import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, X, FolderPlus } from 'lucide-react';
import type { Project } from '@/types';
import { projects as initialProjects } from '@/data/projects';

import { triggerLightTap } from '@/utils/haptics';

export function ProjectManager() {
  const [projects, setProjects] = useState<Project[]>(initialProjects);
  const [isAdding, setIsAdding] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isAdding && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isAdding]);

  const handleAdd = () => {
    if (!newTitle.trim()) return;
    
    triggerLightTap();
    const newProject: Project = {
      id: `project-${Date.now()}`,
      title: newTitle.trim(),
      description: newDescription.trim() || 'Без описания',
    };
    
    setProjects(prev => [...prev, newProject]);
    setNewTitle('');
    setNewDescription('');
    setIsAdding(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleAdd();
    }
    if (e.key === 'Escape') {
      setIsAdding(false);
      setNewTitle('');
      setNewDescription('');
    }
  };

  const handleDelete = (id: string) => {
    triggerLightTap();
    setProjects(prev => prev.filter(p => p.id !== id));
  };

  return (
    <div className="space-y-3">
      <p className="text-[#9B9A97] text-xs mb-4 italic">
        * Примечание: Время на описание проекта в Notion относится к времени реализации как 10:1. 
        Большинство проектов находятся в состоянии квантовой суперпозиции (одновременно делаются и нет).
      </p>
      
      {/* Projects List */}
      <ul className="space-y-2">
        <AnimatePresence mode="popLayout">
          {projects.map((project) => (
            <motion.li
              key={project.id}
              layout
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, x: -20, height: 0 }}
              transition={{ duration: 0.2 }}
              className="group flex items-start gap-2 p-2 rounded-md hover:bg-[rgba(255,255,255,0.03)] transition-colors"
            >
              <span className="text-[#9B9A97] mt-0.5 text-sm">📁</span>
              <div className="flex-1 min-w-0">
                <span className="text-white/90 font-medium text-sm block">{project.title}</span>
                <p className="text-[#9B9A97] text-xs mt-0.5 line-clamp-2">{project.description}</p>
              </div>
              <button
                onClick={() => handleDelete(project.id)}
                className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-red-500/20 text-[#9B9A97] hover:text-red-400 transition-all"
                aria-label={`Удалить проект ${project.title}`}
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </motion.li>
          ))}
        </AnimatePresence>
      </ul>

      {/* Add Form */}
      <AnimatePresence mode="wait">
        {isAdding ? (
          <motion.div
            key="form"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.15 }}
            className="overflow-hidden"
          >
            <div className="p-3 bg-[rgba(255,255,255,0.03)] rounded-lg border border-[rgba(255,255,255,0.08)] space-y-2">
              <div className="flex items-center gap-2">
                <FolderPlus className="w-4 h-4 text-indigo-400 flex-shrink-0" />
                <input
                  ref={inputRef}
                  type="text"
                  value={newTitle}
                  onChange={(e) => setNewTitle(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Название проекта..."
                  className="flex-1 bg-transparent text-white/90 text-sm placeholder:text-[#9B9A97]/50 outline-none"
                  maxLength={50}
                />
              </div>
              <textarea
                value={newDescription}
                onChange={(e) => setNewDescription(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Описание (опционально)..."
                rows={2}
                className="w-full bg-transparent text-[#9B9A97] text-xs placeholder:text-[#9B9A97]/50 outline-none resize-none pl-6"
                maxLength={200}
              />
              <div className="flex justify-end gap-2 pt-1">
                <button
                  onClick={() => {
                    setIsAdding(false);
                    setNewTitle('');
                    setNewDescription('');
                  }}
                  className="px-2.5 py-1 text-xs text-[#9B9A97] hover:text-white transition-colors"
                >
                  Отмена
                </button>
                <button
                  onClick={handleAdd}
                  disabled={!newTitle.trim()}
                  className="px-2.5 py-1 text-xs bg-indigo-500/20 text-indigo-300 rounded hover:bg-indigo-500/30 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  Создать
                </button>
              </div>
            </div>
          </motion.div>
        ) : (
          <motion.button
            key="add-button"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            onClick={() => {
              triggerLightTap();
              setIsAdding(true);
            }}
            className="w-full flex items-center gap-2 p-2 rounded-md text-[#9B9A97] hover:text-white/80 hover:bg-[rgba(255,255,255,0.03)] transition-colors group"
          >
            <Plus className="w-4 h-4 text-[#9B9A97] group-hover:text-indigo-400 transition-colors" />
            <span className="text-xs">Новый проект</span>
          </motion.button>
        )}
      </AnimatePresence>
    </div>
  );
}
